/**
 * The P4 control channel: message types that ride inside the v1 `Envelope`
 * (doc 01 §5). The envelope already fixes `v`, `kind`, `id`, `ts`, `tenant_id`,
 * `account_id`, `seq`, `type` and `payload`; this module fixes the `type`
 * vocabulary and the shape of `payload` for both directions.
 *
 * Two rules the rest of the system depends on:
 *
 * 1. **The client never supplies a price context.** A `PLACE_ORDER` names a
 *    symbol and a volume; the quote, the `at` stamp and the margin book are
 *    supplied by the trade service from its own quote cache. A client that
 *    could send its own `market` snapshot could name its own fill price, and a
 *    client that could send its own `at` could make a stale quote look fresh.
 * 2. **Inbound payloads are strict.** Unknown fields fail. The envelope is
 *    strict too, so a version mismatch surfaces at the edge (doc 01 §5) instead
 *    of being silently ignored.
 *
 * Outbound frames (`RESPONSE`, `EVENT`) are validated by these same schemas in
 * the service's tests, and the schemas are exported so P5/P6 can generate
 * clients from one source of truth rather than from prose.
 */

import { z } from 'zod';

import { ORDER_SIDES, ORDER_TYPES, TIME_IN_FORCES } from '@weke/domain';

import { Envelope } from './envelope.js';
import { Int64String } from './wire.js';

/* ------------------------------------------------------------------ */
/* Vocabulary                                                          */
/* ------------------------------------------------------------------ */

export const CLIENT_MESSAGE_TYPES = ['SUBSCRIBE', 'RESUME', 'COMMAND', 'PING', 'LOGOUT'] as const;
export const SERVER_MESSAGE_TYPES = [
  'HELLO',
  'RESPONSE',
  'EVENT',
  'REPLAY_DONE',
  'PONG',
  'ERROR',
  'ALERT',
] as const;

export const ClientMessageType = z.enum(CLIENT_MESSAGE_TYPES);
export const ServerMessageType = z.enum(SERVER_MESSAGE_TYPES);

/**
 * The `type` of every engine event, in one place. The trade service maps the
 * engine's typed events onto this list; a mismatch is a compile error in
 * `services/trade/src/protocol-map.ts`, not a runtime surprise.
 */
export const ENGINE_EVENT_TYPES = [
  'ORDER_RECEIVED',
  'ORDER_ACCEPTED',
  'ORDER_REJECTED',
  'ORDER_MODIFIED',
  'ORDER_TRIGGERED',
  'ORDER_PARTIALLY_FILLED',
  'ORDER_FILLED',
  'ORDER_CANCELED',
  'ORDER_EXPIRED',
  'DEALER_ACCEPTED',
  'DEAL',
  'POSITION_OPENED',
  'POSITION_MODIFIED',
  'POSITION_VOLUME_CHANGED',
  'POSITION_CLOSED',
  /**
   * A staff cash movement (deposit / withdrawal / credit / correction).
   *
   * Deliberately visible to the account's own client stream: a deposit that
   * appears from nowhere is worse than one you can see, and the event carries the
   * reason and the operator. It is additive — an older client that does not know
   * the type must ignore it rather than fail.
   */
  'BALANCE_ADJUSTED',
  /**
   * P12.3: a booked deal was corrected, restating the position's realised P&L.
   *
   * Additive for the same reason `BALANCE_ADJUSTED` is: the correction moves the
   * client's money, so the client's own stream must show it rather than let the
   * balance change by an unexplained amount. An older client that does not know
   * the type ignores it; the `DEAL` events that accompany it carry the price and
   * the `corrects_deal_id`.
   */
  'POSITION_CORRECTED',
] as const;

export const EngineEventTypeWire = z.enum(ENGINE_EVENT_TYPES);

export const OrderSideWire = z.enum(ORDER_SIDES);
export const OrderTypeWire = z.enum(ORDER_TYPES);
export const TimeInForceWire = z.enum(TIME_IN_FORCES);

/** Protocol-level error codes. Deliberately *not* engine rejection codes. */
export const SERVICE_ERROR_CODES = [
  'E_AUTH',
  'E_TENANT',
  'E_PROTOCOL',
  'E_UNAVAILABLE',
  'E_NOT_FOUND',
  'E_RATE_LIMIT',
  'E_INTERNAL',
  /**
   * P9: the tenant's licence does not grant what was asked (a feature, a new
   * account, a new tenant). A 403 on HTTP and this code on the socket. It is a
   * protocol code, not an engine rejection: nothing was traded.
   */
  'E_ENTITLEMENT',
  /**
   * The per-tenant connection cap. `access` has always sent it; it was missing
   * from this enum, which made a strict client unable to parse the one error that
   * explains why it was refused (docs/11 K-4). Additive, like E_ENTITLEMENT.
   */
  'E_TENANT_QUOTA',
  /**
   * P10: the tenant is `SUSPENDED` (docs/05 §2.1). A suspended tenant may still
   * log in and **close** positions — trapping clients in open positions is the
   * regulatory incident the lifecycle exists to avoid — but new orders and new
   * accounts are refused. Distinct from `E_ENTITLEMENT` because this is a
   * lifecycle state, not a licence capability.
   */
  'E_TENANT_SUSPENDED',
  /**
   * P11.1: the ledger is saturated and the command could not be admitted within
   * the wait budget (`services/trade` + `boundedAcquire`). Distinct from
   * `E_UNAVAILABLE` because nothing was evaluated and the same `cl_ord_id` can
   * be retried immediately: the engine never saw it. Additive, like
   * `E_ENTITLEMENT` and `E_TENANT_QUOTA`.
   */
  'E_BUSY',
] as const;
export const ServiceErrorCode = z.enum(SERVICE_ERROR_CODES);
export type ServiceErrorCode = z.infer<typeof ServiceErrorCode>;

/* ------------------------------------------------------------------ */
/* Client → server                                                     */
/* ------------------------------------------------------------------ */

const SymbolName = z.string().regex(/^[A-Z0-9]{3,20}$/, 'not a symbol name');

export const SubscribePayload = z.strictObject({
  /** Empty means "every symbol the tenant trades". */
  symbols: z.array(SymbolName).max(200).optional(),
  quotes: z.boolean().optional(),
});
export type SubscribePayload = z.infer<typeof SubscribePayload>;

export const ResumePayload = z.strictObject({
  /** The last `account_seq` the client has applied. Replay starts at +1. */
  last_seq: Int64String,
  symbols: z.array(SymbolName).max(200).optional(),
});
export type ResumePayload = z.infer<typeof ResumePayload>;

const MaybePrice = Int64String.nullable().optional();

/**
 * The command body. `at`, `market` and the client's own price context are not
 * accepted — the trade service stamps them (see the module comment).
 */
export const CommandPayload = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('PLACE_ORDER'),
    symbol_id: z.uuid(),
    side: OrderSideWire,
    type: OrderTypeWire,
    tif: TimeInForceWire,
    volume: Int64String,
    limit_price: Int64String.optional(),
    stop_price: Int64String.optional(),
    expected_price: Int64String.optional(),
    sl: Int64String.optional(),
    tp: Int64String.optional(),
    expires_at: z.iso.datetime({ offset: true }).optional(),
  }),
  z.strictObject({
    kind: z.literal('CANCEL_ORDER'),
    order_id: z.uuid(),
    reason: z.string().min(1).max(64).optional(),
  }),
  z.strictObject({
    kind: z.literal('MODIFY_ORDER'),
    order_id: z.uuid(),
    volume: Int64String.optional(),
    limit_price: Int64String.optional(),
    stop_price: Int64String.optional(),
    sl: MaybePrice,
    tp: MaybePrice,
    expires_at: z.iso.datetime({ offset: true }).optional(),
  }),
  z.strictObject({
    kind: z.literal('MODIFY_POSITION'),
    position_id: z.uuid(),
    sl: MaybePrice,
    tp: MaybePrice,
  }),
  z.strictObject({
    kind: z.literal('CLOSE_POSITION'),
    position_id: z.uuid(),
    volume: Int64String.optional(),
  }),
  z.strictObject({
    kind: z.literal('CLOSE_BY'),
    position_id: z.uuid(),
    other_position_id: z.uuid(),
  }),
  z.strictObject({
    kind: z.literal('DEALER_DECISION'),
    order_id: z.uuid(),
    decision: z.enum(['ACCEPT', 'REJECT']),
  }),
]);
export type CommandPayload = z.infer<typeof CommandPayload>;

/** The command envelope: v1 envelope + `cl_ord_id`, `type: 'COMMAND'`. */
export const CommandMessage = Envelope.extend({
  kind: z.literal('command'),
  account_id: z.uuid(),
  cl_ord_id: z.string().min(1).max(36),
  type: z.literal('COMMAND'),
  payload: CommandPayload,
});
export type CommandMessage = z.infer<typeof CommandMessage>;

export const ClientMessage = z.discriminatedUnion('type', [
  Envelope.extend({ kind: z.literal('command'), type: z.literal('SUBSCRIBE'), payload: SubscribePayload }),
  Envelope.extend({ kind: z.literal('command'), type: z.literal('RESUME'), payload: ResumePayload }),
  CommandMessage,
  Envelope.extend({ kind: z.literal('command'), type: z.literal('PING'), payload: z.strictObject({}) }),
  Envelope.extend({
    kind: z.literal('command'),
    type: z.literal('LOGOUT'),
    payload: z.strictObject({}),
  }),
]);
export type ClientMessage = z.infer<typeof ClientMessage>;

/* ------------------------------------------------------------------ */
/* Server → client                                                     */
/* ------------------------------------------------------------------ */

/**
 * One engine event on the wire.
 *
 * `data` is the engine payload with every scaled integer encoded as a canonical
 * decimal string (`wirejson.ts`), so the shape mirrors doc 02 §3.2 /
 * doc 08 §3.2 exactly. Field-by-field integer lists are in
 * `docs/10-p4-handoff.md` §4.
 */
export const EventPayload = z.strictObject({
  account_id: z.uuid(),
  seq: Int64String,
  type: EngineEventTypeWire,
  at: z.iso.datetime({ offset: true }),
  data: z.unknown(),
});
export type EventPayload = z.infer<typeof EventPayload>;

export const ResponsePayload = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('ACCEPTED'),
    orderId: z.uuid(),
    clOrdId: z.string().min(1).max(36),
    idempotentReplay: z.boolean(),
  }),
  z.strictObject({
    kind: z.literal('REJECTED'),
    code: z.string().min(1).max(64),
    reason: z.string().min(1).max(64),
    clOrdId: z.string().min(1).max(36).nullable(),
    orderId: z.uuid().nullable(),
    idempotentReplay: z.boolean(),
  }),
  z.strictObject({
    kind: z.literal('REQUOTE'),
    clOrdId: z.string().min(1).max(36),
    symbolId: z.uuid(),
    requestedPrice: Int64String,
    bid: Int64String,
    ask: Int64String,
    tolerance: Int64String,
    at: z.iso.datetime({ offset: true }),
  }),
  z.strictObject({ kind: z.literal('ORDER_MODIFIED'), orderId: z.uuid() }),
  z.strictObject({
    kind: z.literal('ORDER_CANCELED'),
    orderId: z.uuid(),
    remainingVolume: Int64String,
  }),
  z.strictObject({
    kind: z.literal('ORDER_EXPIRED'),
    orderId: z.uuid(),
    remainingVolume: Int64String,
  }),
  z.strictObject({ kind: z.literal('POSITION_MODIFIED'), positionId: z.uuid() }),
  z.strictObject({
    kind: z.literal('POSITION_CLOSED'),
    positionId: z.uuid(),
    volume: Int64String,
    balanceDeltaMinor: Int64String,
  }),
  z.strictObject({
    kind: z.literal('CLOSE_BY'),
    positionIds: z.tuple([z.uuid(), z.uuid()]),
  }),
  z.strictObject({
    kind: z.literal('QUOTE_APPLIED'),
    triggeredOrders: z.number().int().min(0),
    closedPositions: z.number().int().min(0),
  }),
  z.strictObject({ kind: z.literal('DEALER_ACCEPTED'), orderId: z.uuid() }),
  z.strictObject({
    kind: z.literal('EXTERNAL_FILL_APPLIED'),
    orderId: z.uuid(),
    filledVolume: Int64String,
  }),
  z.strictObject({
    kind: z.literal('ORDERS_EXPIRED'),
    orderIds: z.array(z.uuid()),
  }),
  z.strictObject({
    kind: z.literal('STOP_OUT'),
    closedPositionIds: z.array(z.uuid()),
  }),
  z.strictObject({ kind: z.literal('NOOP'), reason: z.string().max(200) }),
]);
export type ResponsePayload = z.infer<typeof ResponsePayload>;

export const HelloPayload = z.strictObject({
  session_id: z.uuid(),
  tenant_id: z.uuid(),
  account_id: z.uuid().nullable(),
  account_seq: Int64String,
  entitlements_version: z.number().int().min(0),
  server_time: z.iso.datetime({ offset: true }),
  heartbeat_ms: z.number().int().positive(),
  market_data_version: z.literal(1),
  /** Present when the account's engine refused to load (digest mismatch). */
  accepting: z.boolean(),
  refusal: z.string().nullable(),
});
export type HelloPayload = z.infer<typeof HelloPayload>;

export const ReplayDonePayload = z.strictObject({
  replayed_through: Int64String,
  events: z.number().int().min(0),
});
export type ReplayDonePayload = z.infer<typeof ReplayDonePayload>;

export const ServiceErrorPayload = z.strictObject({
  code: ServiceErrorCode,
  message: z.string().min(1),
  cl_ord_id: z.string().min(1).max(36).nullable(),
});
export type ServiceErrorPayload = z.infer<typeof ServiceErrorPayload>;

export const AlertPayload = z.strictObject({
  kind: z.enum([
    'MARGIN_CALL',
    'STOP_OUT',
    'UNVALUABLE_BOOK',
    'ROLLOVER_SKIPPED',
    'ENGINE_REFUSED',
    'FEED_STALE',
  ]),
  account_id: z.uuid(),
  detail: z.string().min(1).max(500),
});
export type AlertPayload = z.infer<typeof AlertPayload>;

/** Parse a client frame. Throws `ZodError`; the edge maps it to `E_PROTOCOL`. */
export function parseClientMessage(frame: unknown): ClientMessage {
  return ClientMessage.parse(frame);
}

/** Parse a server frame (used by the smoke client, the bench and the tests). */
export const ServerMessage = z.discriminatedUnion('type', [
  Envelope.extend({ kind: z.literal('event'), type: z.literal('HELLO'), payload: HelloPayload }),
  Envelope.extend({ kind: z.literal('response'), type: z.literal('RESPONSE'), payload: ResponsePayload }),
  Envelope.extend({ kind: z.literal('event'), type: z.literal('EVENT'), payload: EventPayload }),
  Envelope.extend({
    kind: z.literal('event'),
    type: z.literal('REPLAY_DONE'),
    payload: ReplayDonePayload,
  }),
  Envelope.extend({ kind: z.literal('event'), type: z.literal('PONG'), payload: z.strictObject({}) }),
  Envelope.extend({ kind: z.literal('error'), type: z.literal('ERROR'), payload: ServiceErrorPayload }),
  Envelope.extend({ kind: z.literal('event'), type: z.literal('ALERT'), payload: AlertPayload }),
]);
export type ServerMessage = z.infer<typeof ServerMessage>;

export function parseServerMessage(frame: unknown): ServerMessage {
  return ServerMessage.parse(frame);
}

